import { UserRole } from './auth';

/**
 * Navigation item configuration
 * Maps to Req 76 AC 2: role-based navigation
 */
export interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon?: string;
  roles: UserRole[];
  children?: NavigationItem[];
  /**
   * Sidebar section this item belongs to. A flat list of ten destinations
   * gives no sense of which ones relate to each other; grouping turns it into
   * three short, scannable sets.
   */
  group?: NavigationGroup;
  /**
   * One-line gloss shown beside the page title in the app header.
   *
   * Lives here rather than in each page body so the title and its description
   * are defined once. Every string below is the page's own existing subtitle
   * text, moved - not newly invented copy.
   */
  description?: string;
}

export type NavigationGroup = 'overview' | 'optimize' | 'manage';

export const NAVIGATION_GROUP_LABELS: Record<NavigationGroup, string> = {
  overview: 'Overview',
  optimize: 'Optimize',
  manage: 'Manage',
};

/** Render order for the sidebar's section groups. */
export const NAVIGATION_GROUP_ORDER: NavigationGroup[] = ['overview', 'optimize', 'manage'];

/**
 * Navigation configuration by role
 * Maps to Req 166 AC 1: persona-driven default views
 * 
 * Role access:
 * - Admin: full access to all views
 * - Analyst: can view everything + acknowledge anomalies, no admin/edit tags
 * - Viewer: read-only access to dashboards, no admin or edit capabilities
 */
export const NAVIGATION_CONFIG: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    roles: [UserRole.ADMIN, UserRole.ANALYST, UserRole.VIEWER],
    group: 'overview',
    description: 'Executive summary of spend, savings and forecast',
  },
  {
    id: 'cost-explorer',
    label: 'Cost Explorer',
    path: '/cost-explorer',
    roles: [UserRole.ADMIN, UserRole.ANALYST, UserRole.VIEWER],
    group: 'overview',
    description: 'Multi-dimensional cost analysis with drill-down capabilities',
  },
  {
    id: 'recommendations',
    label: 'Recommendations',
    path: '/recommendations',
    roles: [UserRole.ADMIN, UserRole.ANALYST, UserRole.VIEWER],
    group: 'optimize',
    description: 'Real-time recommendations across AWS, Azure, and GCP cost analysis',
  },
  {
    id: 'what-if-simulator',
    label: 'What-If Simulator',
    path: '/what-if-simulator',
    roles: [UserRole.ADMIN, UserRole.ANALYST, UserRole.VIEWER],
    group: 'optimize',
    description: 'Model the cost impact of a change before making it',
  },
  {
    id: 'policies',
    label: 'Policies',
    path: '/policies',
    roles: [UserRole.ADMIN, UserRole.ANALYST],
    group: 'optimize',
    description: 'Author cost-governance rules as policy-as-code',
  },
  {
    id: 'tagging',
    label: 'Tagging',
    path: '/tagging',
    roles: [UserRole.ADMIN, UserRole.ANALYST, UserRole.VIEWER],
    group: 'optimize',
    description: 'Multi-cloud resource tagging policy enforcement',
  },
    {
    id: 'anomalies',
    label: 'Anomalies',
    path: '/anomalies',
    roles: [UserRole.ADMIN, UserRole.ANALYST, UserRole.VIEWER],
    group: 'optimize',
    description: 'Detect and investigate unusual cost patterns',
  },
  {
    id: 'invoice',
    label: 'Invoice',
    path: '/invoice',
    roles: [UserRole.ADMIN, UserRole.ANALYST, UserRole.VIEWER],
    group: 'overview',
    description: 'Multi-cloud invoice with service, account, and chargeback breakdowns',
  },
  {
    id: 'chat-history',
    label: 'Chat History',
    path: '/chat-history',
    roles: [UserRole.ADMIN, UserRole.ANALYST, UserRole.VIEWER],
    group: 'manage',
    description: 'Browse and resume your past conversations with the AI Assistant',
  },
  {
    id: 'admin',
    label: 'Admin Console',
    path: '/admin',
    roles: [UserRole.ADMIN],
    group: 'manage',
    description: 'Manage tenant configuration, cloud connections, users, and platform settings',
  },
];
